import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import {
  ContentTab,
  Content,
  ContentType,
  ExtrasContent,
} from '../model/extras.domain';
import { useExtrasModel } from '../model/extras.model';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import {
  filterAccessOptions,
  filterContentTree,
  filterExtrasVM,
  filterTypeOptions,
  filterVisibilityOptions,
  getContentName,
  toCreateContentInput,
  toExtrasMenuItems,
  toUpdateContentInput,
} from './extras.mappers';
import { useUser } from '@client/new/hooks/use-user';
import { mixTrack } from '@services/mixpanel';
import { ProfileData } from '@design-system/components/mobile-preview/types';
import { v4 } from 'uuid';
import { selector } from '../model/tree';
import { PDFViewModel, usePDFViewModel } from '../pdf/pdf.viewModel';
import { scrollToElement } from '@client/new/pages/workout-editor/view-model/form.utils';
import {
  SlidesViewModel,
  useSlidesViewModel,
} from '../slides/slides.viewModel';
import { useVideoViewModel, VideoViewModel } from '../video/video.viewModel';
import { LinkViewModel, useLinkViewModel } from '../link/link.viewModel';
import {
  LegacyGuideViewModel,
  useLegacyGuideViewModel,
} from '../legacy-guide/legacy-guide.viewModel';
import { useProcessPendingElements } from './extras.utils';
import { useKeepScrollPosition } from '@client/new/hooks/useKeepScrollPosition';
import { BasicMenuItem } from '@client/new/components/BasicMenu/BasicMenu';
import {
  handleCloseComments,
  handleCloseModal,
  handleConfirmDelete,
  handleDelete,
  handleOpenComments,
  handleReorder,
  handleSaveTab,
  handleSetFilterType,
  handleSetSearch,
  handleSubmitAction,
  handleToggleItemVisible,
} from './extras.actions';
import { createContentActionRegistry } from './contentActions';
import { ModalMode, Draggable } from './extras.types';
import { ContentViewModel } from './extras.viewModel.types';
import { buildContentViewModel } from './buildContentViewModel';

export type Filters = {
  type: (typeof filterTypeOptions)[number]['value'] | '';
  access: (typeof filterAccessOptions)[number]['value'] | '';
  visibility: (typeof filterVisibilityOptions)[number]['value'] | '';
};

export type ExtrasVMActionMap = {
  Archive: string;
  ConfirmArchive: null;
  Unarchive: string;
  ConfirmUnarchive: null;
  SellAsSubscription: string;
  ConfirmSellAsSubscription: null;
  SellAsAnyPurchase: string;
  ConfirmSellAsAnyPurchase: null;
  Create: ContentType;
  Edit: string;
  Delete: string;
  ToggleVisiblity: null;
  SaveTab: string;
  SetSearch: string;
  Reorder: Draggable[];
  SetFilter: Partial<Filters>;
  ToggleItemVisible: string;
  CloseModal: null;
  CreateContent: Content;
  ConfirmDelete: null;
  Submit: Content;
  OpenComments: { contentId: string };
  CloseComments: null;
  TogglePreview: null;
};

export type ExtrasVMAction = Actions<ExtrasVMActionMap>;

export type ExtrasVM = {
  search: string;
  filters: Filters;
  modalMode: ModalMode;
  formType: ContentType;
  currentId: string;
  isExtrasEmpty: boolean;
  isBundleCreator: boolean;
  profile: ProfileData;
  tabs: Array<ContentTab['value']>;
  primaryColor: string;
  pdfVM: PDFViewModel;
  slidesVM: SlidesViewModel;
  videoVM: VideoViewModel;
  linkVM: LinkViewModel;
  legacyGuideVM: LegacyGuideViewModel;
  isMutationRunning: boolean;
  commentsContentId: string | null;
  extrasMenu: BasicMenuItem[];
  showPreview: boolean;
  contentViewModels?: ContentViewModel[];
  filteredContentMap: ExtrasContent['contentMap'];
  filteredContentIds: ExtrasContent['contentIds'];
} & Dispatchers<ExtrasVMActionMap> &
  ExtrasContent;

export const useExtrasVM = (): Async<ExtrasVM> => {
  const influencerId = useInfluencerId();

  const [state, setState] = useState<ExtrasContent>({
    contentIds: {
      id: 'empty',
    },
    contentMap: {},
    childrenMap: {},
  });

  const model = useExtrasModel({
    creatorId: influencerId,
  });
  const pdfVM = usePDFViewModel({ model });
  const slidesVM = useSlidesViewModel({ model });
  const videoVM = useVideoViewModel({ model });
  const linkVM = useLinkViewModel({ model });
  const legacyGuideVM = useLegacyGuideViewModel({ model });
  useProcessPendingElements(model);
  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<Filters>({
    type: '',
    access: '',
    visibility: '',
  });
  const { roleLite } = useUser();
  const [currentId, setCurrentId] = useState<string>('');
  const [modalModeType, setModalModeType] = useState<ModalMode>('none');

  const [commentsContentId, setCommentsContentId] = useState<string | null>(
    null,
  );

  const [showPreview, setShowPreview] = useState<boolean>(() => {
    const saved = localStorage.getItem('extras-preview-visible');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useKeepScrollPosition(currentId);

  const [formType, setFormType] = useState<ContentType>('ContentLink');
  const mainTab = state.contentIds?.items?.[0];
  const topLevelItems = mainTab?.items ?? [];
  const isExtrasEmpty = topLevelItems.length === 0;
  const canDeleteExtras = roleLite !== 'user';

  const handleSubmit = async (data: Content): Promise<unknown> => {
    if (model.data.type !== 'fetched')
      return Promise.reject(new Error('Model not fetched'));

    if (modalModeType === 'create') {
      const parentId = mainTab?.id || '';

      await model.create({
        input: toCreateContentInput(data, parentId),
      });

      setModalModeType('none');
      setFormType('ContentLink');

      mixTrack({
        type: 'Extras Block Created',
        payload: {
          type: formType,
          title: getContentName(data),
        },
      });

      scrollToElement(`${data.value.id}`, 200);
      return;
    }

    if (modalModeType === 'edit' && currentId) {
      await model.update(currentId, toUpdateContentInput(data));

      mixTrack({
        type: 'Extras Block Saved',
        payload: {
          type: formType,
          title: getContentName(data),
          'video time': 0,
          'video aspect ratio': 'n/a',
          'Comments on': false,
        },
      });
      return;
    }

    return Promise.reject(new Error('Invalid modal mode or currentId'));
  };

  useEffect(() => {
    if (model.data.type !== 'fetched') return;
    if (model.data.value.contentIds.id === 'empty') {
      return dispatchers.dispatch('Create', 'ContentTab');
    }
  }, [model.data.type]);

  useEffect(() => {
    if (model.data.type !== 'fetched') return;
    const contentIds = model.data.value.contentIds;
    const childrenMap = model.data.value.childrenMap;
    const contentMap = model.data.value.contentMap;
    setState(() => ({
      contentIds,
      contentMap,
      childrenMap,
    }));
  }, [JSON.stringify(model.data)]);

  const contentActions = useMemo(() => {
    if (
      pdfVM.type !== 'fetched' ||
      slidesVM.type !== 'fetched' ||
      videoVM.type !== 'fetched' ||
      linkVM.type !== 'fetched' ||
      legacyGuideVM.type !== 'fetched'
    )
      return null;

    return createContentActionRegistry({
      pdfViewModel: pdfVM.value,
      slidesViewModel: slidesVM.value,
      videoViewModel: videoVM.value,
      linkViewModel: linkVM.value,
      legacyGuideViewModel: legacyGuideVM.value,
    });
  }, [pdfVM, slidesVM, videoVM, linkVM, legacyGuideVM]);

  const openCreateModal = (contentType: ContentType) => {
    const id = v4();
    setModalModeType('create');
    setFormType(contentType);
    setCurrentId(id);
  };

  const openEditModal = (contentType: ContentType, contentId: string) => {
    setModalModeType('edit');
    setFormType(contentType);
    setCurrentId(contentId);
  };

  const _dispatch = (a: ExtrasVMAction) => {
    if (model.data.type !== 'fetched') return;
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'SaveTab': {
        handleSaveTab(a.payload, {
          modalModeType,
          currentId,
          model,
          setModalModeType,
          setFormType,
        });
        return;
      }
      case 'SetSearch': {
        handleSetSearch(a.payload, { setSearch });
        return;
      }
      case 'SetFilter': {
        handleSetFilterType(a.payload, { filters, setFilters });
        return;
      }
      case 'OpenComments': {
        handleOpenComments(a.payload.contentId, { setCommentsContentId });
        return;
      }
      case 'CloseComments': {
        handleCloseComments({ setCommentsContentId });
        return;
      }
      case 'Create': {
        if (!contentActions) return;

        contentActions.create(a.payload, {
          parentId: mainTab?.id || '',
          openCreateModal,
        });
        return;
      }
      case 'CloseModal': {
        handleCloseModal({ setModalModeType, setFormType, setCurrentId });
        return;
      }
      case 'Edit': {
        const contentId = a.payload;
        const content = model.data.value.contentMap[contentId];
        setCurrentId(contentId);

        if (!contentActions || !content) return;

        contentActions.edit({
          content,
          contentId,
          roleLite,
          openEditModal,
        });
        return;
      }
      case 'ToggleItemVisible': {
        handleToggleItemVisible(a.payload, {
          state,
          setState,
          setCurrentId,
          model,
        });
        return;
      }
      case 'Reorder': {
        handleReorder(a.payload, { setState, model });
        return;
      }
      case 'Delete': {
        handleDelete(a.payload, { setCurrentId, setModalModeType });
        return;
      }

      case 'Submit': {
        handleSubmitAction(a.payload, { handleSubmit, currentId });
        return;
      }

      case 'ConfirmDelete': {
        handleConfirmDelete({
          currentId,
          state,
          model,
          setModalModeType,
          setCurrentId,
        });
        return;
      }

      case 'TogglePreview': {
        setShowPreview((prev) => {
          const newValue = !prev;
          localStorage.setItem(
            'extras-preview-visible',
            JSON.stringify(newValue),
          );
          return newValue;
        });
        return;
      }

      case 'Archive': {
        setModalModeType('archive');
        setCurrentId(a.payload);
        return;
      }

      case 'ConfirmArchive': {
        const content = state.contentMap[currentId];
        if (!content) {
          toast.error('Content not found');
          return;
        }

        model
          .archiveContent({
            itemId: currentId,
            type: content.type,
          })
          .then(() => {
            const contentName = getContentName(content);
            toast.success(`"${contentName}" has been archived`);

            setModalModeType('none');
            setCurrentId('');
          })
          .catch((err) => {
            toast.error(
              `Failed to archive content: ${err?.message || 'Unknown error'}`,
            );
          });
        return;
      }

      case 'Unarchive': {
        setModalModeType('unarchive');
        setCurrentId(a.payload);
        return;
      }

      case 'ConfirmUnarchive': {
        const content = state.contentMap[currentId];
        if (!content) {
          toast.error('Content not found');
          return;
        }

        model
          .unarchiveContent({
            itemId: currentId,
            type: content.type,
          })
          .then(() => {
            const contentName = getContentName(content);
            toast.success(`"${contentName}" has been unarchived`);

            setModalModeType('none');
            setCurrentId('');
          })
          .catch((err) => {
            toast.error(
              `Failed to unarchive content: ${err?.message || 'Unknown error'}`,
            );
          });
        return;
      }

      case 'SellAsSubscription': {
        setModalModeType('sell-as-subscription');
        setCurrentId(a.payload);
        return;
      }

      case 'ConfirmSellAsSubscription': {
        const content = state.contentMap[currentId];
        if (!content) {
          toast.error('Content not found');
          return;
        }

        model
          .sellAsSubscription({
            itemId: currentId,
            type: content.type,
          })
          .then(() => {
            const contentName = getContentName(content);
            toast.success(`"${contentName}" is now available via subscription`);

            setModalModeType('none');
            setCurrentId('');
          })
          .catch((err) => {
            toast.error(
              `Failed to update content: ${err?.message || 'Unknown error'}`,
            );
          });
        return;
      }

      case 'SellAsAnyPurchase': {
        setModalModeType('sell-as-any-purchase');
        setCurrentId(a.payload);
        return;
      }

      case 'ConfirmSellAsAnyPurchase': {
        const content = state.contentMap[currentId];
        if (!content) {
          toast.error('Content not found');
          return;
        }

        model
          .sellAsAnyPurchase({
            itemId: currentId,
            type: content.type,
          })
          .then(() => {
            const contentName = getContentName(content);
            toast.success(
              `"${contentName}" is now available with any purchase`,
            );

            setModalModeType('none');
            setCurrentId('');
          })
          .catch((err) => {
            toast.error(
              `Failed to update content: ${err?.message || 'Unknown error'}`,
            );
          });
        return;
      }
    }
  };

  const dispatchers = getDispatchers<ExtrasVMActionMap>(_dispatch);

  if (model.data.type !== 'fetched' && state.contentIds.id === 'empty')
    return model.data;
  if (model.profile.type !== 'fetched') return model.profile;

  const tabs = selector(
    state.contentIds,
    state.contentMap,
    (v) => v.type === 'ContentTab',
  ).map((v) => v.value);

  const primaryColor = `#${
    model.profile.value.customColorSchema?.primaryColor ?? '000'
  }`;

  if (pdfVM.type !== 'fetched') return pdfVM;
  if (slidesVM.type !== 'fetched') return slidesVM;
  if (videoVM.type !== 'fetched') return videoVM;
  if (linkVM.type !== 'fetched') return linkVM;
  if (legacyGuideVM.type !== 'fetched') return legacyGuideVM;

  // Check if creator is OTP-only (offering contains only 'otp')
  const isOtpOnly =
    model.profile.value.offering?.length === 1 &&
    model.profile.value.offering[0] === 'otp';

  const contentViewModels: ContentViewModel[] | undefined = Object.values(
    state.contentMap,
  )
    .filter((content) => content.type !== 'ContentTab')
    .map((content) =>
      buildContentViewModel({
        content,
        isItemMutating: model.isItemMutating(content.value.id),
        isBundleCreator: model.isBundleCreator,
        isOtpOnly,
        dispatch: dispatchers.dispatch,
      }),
    );

  const filteredContentMap = filterExtrasVM(
    {
      contentIds: state.contentIds,
      contentMap: state.contentMap,
      childrenMap: state.childrenMap,
    },
    search,
    filters,
  );

  const filteredContentIds = filterContentTree(
    state.contentIds,
    filteredContentMap,
  );

  return toAsyncFetched({
    profile: model.profile.value,
    isExtrasEmpty,
    isBundleCreator: model.isBundleCreator,
    canDeleteExtras,
    search,
    filters,
    formType,
    currentId,
    tabs,
    primaryColor,
    modalMode: modalModeType,
    commentsContentId,
    showPreview,
    ...dispatchers,
    ...state,
    pdfVM: pdfVM.value,
    slidesVM: slidesVM.value,
    videoVM: videoVM.value,
    linkVM: linkVM.value,
    legacyGuideVM: legacyGuideVM.value,
    isMutationRunning: model.isMutationRunning,
    extrasMenu: toExtrasMenuItems(dispatchers.dispatch),
    contentViewModels,
    filteredContentMap,
    filteredContentIds,
  });
};
